import moment from "moment";
import {
    getExpensesByLevels,
    getExpensesSummaryCards,
    getExpensesCharts,
    getExpensesComparison,
} from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";
import { normalizeSalesCharts, deriveByClientSeries } from "./utils/salesCharts";

// Cuentas level_2 cuya suma forma el numerador del ratio "% Gastos / Ventas".
// Coincide con el cálculo de `gastos/ventas_%` en ResultadosTab (ver
// reportStructure.js: keysSum = ['gastos_administracion', 'gastos_comerciales',
// 'gastos_i+d']), excluyendo `gastos_no_operacional` a propósito.
const EFFICIENCY_L2_ACCOUNTS = [
    "Gastos De Administración",
    "Gastos Comerciales",
    "Gastos I+D",
];

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = () => ({
    /**
     * Dimensión activa del header (mismo modelo que Ventas): "cuentas" (plan
     * de cuentas), el nombre de una categoría de clasificación (tiendas,
     * marcas, …) o "contrapartes" (breakdown por tipo de contraparte). Define
     * el group_by de la tabla y los gráficos.
     */
    activeSubFilter: "cuentas",
    selectedCounterpartTypes: [],
    /**
     * Filtro multi-select del header: etiquetas L3 (cuentas o contrapartes)
     * elegidas. Vacío = sin filtro (se muestran todas). Acota las filas
     * visibles de la tabla y enfoca los gráficos del header en esos items.
     */
    selectedItems: [],
    monthHeaders: [],
    accountsData: {},
    /**
     * Monto del mismo bucket 12 meses atrás, por etiqueta (L2/L3) y período.
     * Alimenta el tooltip YoY de la tabla. `null` cuando no hay peer hace 12m.
     * @type {Object.<string, Object.<string, number | null>>}
     */
    prevYearData: {},
    level2Accounts: [],
    level3Accounts: [],
    hierarchy: {},
    /**
     * Tipo de contraparte representativo por label (alias), sólo poblado en
     * modo contrapartes. Lo usa GastosTab para abrir el detalle de la
     * contraparte (modal 2) con la obligación correcta. { "ACME": "Proveedor" }.
     */
    counterpartTypes: {},
    /** Total de ventas netas por etiqueta de período, ej. { "Ene 2025": 1234567, ... } */
    salesByPeriod: {},
    /**
     * Acumulado del periodo + comparación contra ventana previa de igual largo.
     * Indexado por L2/L3 + clave reservada "__total__" para "Total Gastos".
     * `pct` es null cuando prevAcc === 0 (frontend muestra "—").
     * @type {Object.<string, { acc: number, prevAcc: number, pct: number | null }>}
     */
    accumulatedData: {},
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    /**
     * Resumen para las summary cards: { current_month, ytd, ltm }.
     * Cada entrada incluye period_start, period_end, amount y ratios mom/yoy.
     * Se calcula independiente de los filtros de la tabla porque necesita
     * comparables que pueden quedar fuera del rango visible (LTM, YoY, etc.).
     */
    summaryCards: null,
    isSummaryLoading: false,
    summaryError: null,
    /**
     * Series de los dos gráficos del header (stacked + líneas por contraparte/
     * cuenta). Normalizado por `normalizeSalesCharts` (mismo shape que Ventas
     * para reusar SalesChartsRow). null antes del primer fetch.
     */
    chartsData: null,
    isChartsLoading: false,
    chartsError: null,
    /**
     * Comparación año-contra-año (gráfico que reemplaza la tabla al togglear
     * "Comparación"). `comparisonData` = { months, series, availableYears }.
     * `comparisonYears` = años elegidos (default [añoActual, añoActual-1]).
     * Gastos no tiene subcategoría, así que no hay comparisonItem.
     */
    comparisonData: null,
    isComparisonLoading: false,
    comparisonError: null,
    comparisonYears: [],
});

const EMPTY_ACC = { acc: 0, prevAcc: 0, pct: null };

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    isCuentasMode: (state) => state.activeSubFilter === "cuentas",

    // group_by que comparten la tabla y los gráficos del header. "cuentas" o
    // el nombre de la categoría se mandan tal cual al backend (el selector
    // resuelve la categoría como Attribute); en contrapartes mandamos los
    // tipos seleccionados unidos por coma (todos por default vía
    // select-all-on-open del dropdown). El endpoint de charts agrupa por la
    // entidad contraparte filtrada por esos tipos (top-N + "Otros"), así que
    // tabla y gráficos quedan sobre la misma dimensión.
    groupByParam: (state) => {
        if (state.activeSubFilter === "contrapartes") {
            return state.selectedCounterpartTypes.join(",");
        }
        return state.activeSubFilter;
    },

    // Payload de los gráficos del header, listo para SalesChartsRow. Sin
    // selección usa la serie top-N + "Otros" del endpoint `charts`. Con
    // selección activa deriva las series client-side de `accountsData` acotadas
    // a los items elegidos (mismo patrón que Ventas) — así el filtro mueve
    // tabla y gráficos en sincronía sin refetch.
    headerChartsData: (state) => {
        if (!state.selectedItems.length) return state.chartsData;
        return deriveByClientSeries(
            state.accountsData,
            state.monthHeaders,
            state.selectedItems,
        );
    },

    // Etiquetas L3 visibles según el filtro: las seleccionadas o todas cuando
    // no hay selección. Lo consume GastosTab para acotar las filas.
    visibleItemSet: (state) =>
        state.selectedItems.length ? new Set(state.selectedItems) : null,

    tableRows: (state) => {
        // En contrapartes la tabla es plana: una fila por contraparte, sin
        // anidar bajo cuentas. report_data/accumulatedData ya agregan cada
        // alias entre las cuentas en que aparece, así que iteramos
        // level3Accounts (deduplicado) y ordenamos por acumulado descendente
        // (las contrapartes más grandes primero). level 1 = fila plana: el
        // row component no la indenta ni le pone chevron (eso es level 2/3).
        if (state.activeSubFilter === "contrapartes") {
            return state.level3Accounts
                .map((label) => {
                    const acc = state.accumulatedData[label] ?? EMPTY_ACC;
                    return {
                        label,
                        level: 1,
                        isTotal: false,
                        values: state.monthHeaders.map(
                            (header) => state.accountsData[label]?.[header] ?? 0,
                        ),
                        prevYearValues: state.monthHeaders.map(
                            (header) => state.prevYearData[label]?.[header] ?? null,
                        ),
                        accumulated: acc.acc,
                        pct: acc.pct,
                    };
                })
                .sort((a, b) => b.accumulated - a.accumulated);
        }

        const rows = [];

        state.level2Accounts.forEach(l2 => {
            // Fila de Nivel 2
            const l2Acc = state.accumulatedData[l2] ?? EMPTY_ACC;
            rows.push({
                label: l2,
                level: 2,
                isTotal: false,
                values: state.monthHeaders.map(
                    (header) => state.accountsData[l2]?.[header] ?? 0
                ),
                prevYearValues: state.monthHeaders.map(
                    (header) => state.prevYearData[l2]?.[header] ?? null
                ),
                accumulated: l2Acc.acc,
                pct: l2Acc.pct,
            });

            // Filas de Nivel 3 para este Nivel 2
            const children = state.hierarchy[l2] || [];
            children.forEach(l3 => {
                // Evitar duplicados si el nombre es igual al nivel 2
                if (l3 === l2) return;

                const l3Acc = state.accumulatedData[l3] ?? EMPTY_ACC;
                rows.push({
                    label: l3,
                    level: 3,
                    parent: l2,
                    isTotal: false,
                    values: state.monthHeaders.map(
                        (header) => state.accountsData[l3]?.[header] ?? 0
                    ),
                    prevYearValues: state.monthHeaders.map(
                        (header) => state.prevYearData[l3]?.[header] ?? null
                    ),
                    accumulated: l3Acc.acc,
                    pct: l3Acc.pct,
                });

            });
        });

        return rows;
    },

    totalRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            return state.level2Accounts.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
        });

        // Peer del año anterior del total = suma de prev_year por bucket sobre
        // las L2. null cuando ninguna L2 tenía peer hace 12m (mismo criterio
        // que la fila total de Ventas).
        const prevYearValues = state.monthHeaders.map((header) => {
            let sum = 0;
            let anyPeer = false;
            state.level2Accounts.forEach((account) => {
                const peer = state.prevYearData[account]?.[header];
                if (peer != null) {
                    sum += peer;
                    anyPeer = true;
                }
            });
            return anyPeer ? sum : null;
        });

        const totals = state.accumulatedData.__total__ ?? EMPTY_ACC;
        return {
            label: "Total Gastos",
            values,
            prevYearValues,
            isTotal: true,
            accumulated: totals.acc,
            pct: totals.pct,
        };
    },

    /**
     * Ratio gastos operacionales / ventas netas por período. Numerador =
     * suma de las level_2 en EFFICIENCY_L2_ACCOUNTS (no usa totalRow porque
     * ese podría incluir 'Gastos No Operacionales', inconsistente con el
     * ratio mostrado en Estado de Resultados). Valores como ratio decimal
     * (0.12 = 12%); se formatea en la UI con formatPercent.
     */
    ratioRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            const gastos = EFFICIENCY_L2_ACCOUNTS.reduce((sum, account) => {
                const val = Number(state.accountsData[account]?.[header] || 0);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
            const ventas = Number(state.salesByPeriod[header] || 0);
            return ventas !== 0 ? gastos / ventas : 0;
        });
        return { label: "% Gastos / Ventas", values, isRatio: true };
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_ACTIVE_SUB_FILTER(state, filter) {
        state.activeSubFilter = filter;
        // Cambia la dimensión → el filtro de items deja de aplicar. Fuera de
        // contrapartes limpiamos también los tipos, así reentrar a esa
        // dimensión arranca con todos marcados (select-all-on-open del dropdown).
        state.selectedItems = [];
        if (filter !== "contrapartes") state.selectedCounterpartTypes = [];
    },
    SET_SELECTED_COUNTERPART_TYPES(state, types) {
        state.selectedCounterpartTypes = Array.isArray(types) ? [...types] : [];
        // Elegir tipos = dimensión contrapartes; vaciar = volver a cuentas
        // (misma semántica histórica donde [] significaba modo cuentas).
        state.activeSubFilter = state.selectedCounterpartTypes.length
            ? "contrapartes"
            : "cuentas";
        // Cambia la dimensión, así que los items seleccionados dejan de aplicar.
        state.selectedItems = [];
    },
    SET_SELECTED_ITEMS(state, items) {
        state.selectedItems = Array.isArray(items) ? [...items] : [];
    },
    SET_EXPENSES_DATA(state, { level2Accounts, level3Accounts, hierarchy, counterpartTypes, accountsData, prevYearData, monthHeaders, periodRange, salesByPeriod, accumulatedData }) {
        state.level2Accounts = level2Accounts;
        state.level3Accounts = level3Accounts;
        state.hierarchy = hierarchy;
        state.counterpartTypes = counterpartTypes || {};
        state.accountsData = accountsData;
        state.prevYearData = prevYearData || {};
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
        state.salesByPeriod = salesByPeriod || {};
        state.accumulatedData = accumulatedData || {};
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SUMMARY_CARDS(state, summary) {
        state.summaryCards = summary;
    },
    SET_SUMMARY_LOADING(state, isLoading) {
        state.isSummaryLoading = isLoading;
    },
    SET_SUMMARY_ERROR(state, error) {
        state.summaryError = error;
    },
    SET_CHARTS_DATA(state, payload) {
        state.chartsData = payload;
    },
    SET_CHARTS_LOADING(state, isLoading) {
        state.isChartsLoading = isLoading;
    },
    SET_CHARTS_ERROR(state, error) {
        state.chartsError = error;
    },
    SET_COMPARISON_DATA(state, payload) {
        state.comparisonData = payload;
    },
    SET_COMPARISON_LOADING(state, isLoading) {
        state.isComparisonLoading = isLoading;
    },
    SET_COMPARISON_ERROR(state, error) {
        state.comparisonError = error;
    },
    SET_COMPARISON_YEARS(state, years) {
        state.comparisonYears = years;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    async fetchExpensesData({ getters, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
            );

            const response = await getExpensesByLevels(initDate, endDate, getters.groupByParam, filterPeriod);
            const {
                report_data,
                level2_accounts,
                level3_accounts,
                hierarchy,
                counterpart_types,
                sales_by_period,
                accumulated_data: accumulatedRaw,
            } = response.data;

            // Cabeceras según granularidad (mes/trimestre/año).
            const monthHeaders = buildHeadersByPeriod(initDate, endDate, filterPeriod);
            const accountsData = {};
            const prevYearData = {};

            // El backend ya devuelve una fila por bucket truncado (DATE_TRUNC)
            // y aplica el cutoff YTD vía ytd_month_limit; acá solo formateamos.
            // `prev_year_amount` (peer 12m atrás) alimenta el tooltip YoY; null
            // se preserva (distinto del 0 explícito).
            Object.keys(report_data).forEach(accountName => {
                accountsData[accountName] = {};
                prevYearData[accountName] = {};
                report_data[accountName].forEach(row => {
                    const header = formatPeriodLabel(row.period, filterPeriod);
                    const amount = Number(row.total_amount);
                    accountsData[accountName][header] = isNaN(amount) ? 0 : amount;
                    const peer = row.prev_year_amount;
                    prevYearData[accountName][header] = peer == null ? null : Number(peer);
                });

            });

            // Total de ventas netas por etiqueta de período → input al ratio
            // "% Gastos / Ventas". Misma normalización de label que accountsData.
            const salesByPeriod = {};
            (sales_by_period || []).forEach(row => {
                const header = formatPeriodLabel(row.period, filterPeriod);
                const amount = Number(row.total_amount);
                salesByPeriod[header] = isNaN(amount) ? 0 : amount;
            });

            // Normaliza snake→camel y castea a Number (Decimal puede llegar
            // como string desde algunos paths del selector).
            const accumulatedData = {};
            Object.entries(accumulatedRaw ?? {}).forEach(([key, value]) => {
                accumulatedData[key] = {
                    acc: Number(value?.acc ?? 0),
                    prevAcc: Number(value?.prev_acc ?? 0),
                    pct: value?.pct == null ? null : Number(value.pct),
                };
            });

            commit("SET_EXPENSES_DATA", {
                level2Accounts: level2_accounts,
                level3Accounts: level3_accounts,
                hierarchy,
                counterpartTypes: counterpart_types || {},
                accountsData,
                prevYearData,
                monthHeaders,
                periodRange: { init_date: initDate, end_date: endDate },
                salesByPeriod,
                accumulatedData,
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[expensesReport]", silent: true });
            commit("SET_ERROR", "Error al cargar gastos.");
        } finally {
            commit("SET_LOADING", false);
        }
    },

    // Gráficos del header (stacked + líneas por contraparte/cuenta/categoría).
    // Endpoint dedicado (charts) — mismo rango/granularidad y misma dimensión
    // que la tabla (groupByParam): 'cuentas', nombre de categoría, o los tipos
    // de contraparte seleccionados. Normalización compartida con Ventas.
    async fetchExpensesCharts({ getters, commit, rootState }, payload = {}) {
        commit("SET_CHARTS_LOADING", true);
        commit("SET_CHARTS_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;
            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
            );
            const groupBy = getters.groupByParam;

            const response = await getExpensesCharts(
                initDate,
                endDate,
                filterPeriod,
                payload.topN,
                groupBy,
            );
            commit("SET_CHARTS_DATA", normalizeSalesCharts(response.data, filterPeriod));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[expensesReport]", silent: true });
            commit(
                "SET_CHARTS_ERROR",
                extractErrorMessage(error, "Error al cargar gráficos de gastos."),
            );
        } finally {
            commit("SET_CHARTS_LOADING", false);
        }
    },

    // Comparación año-contra-año del gasto total (lazy: GastosTab la dispara al
    // abrir el panel y al cambiar el mes mientras está abierto). Gastos no tiene
    // subcategoría → siempre sin group_by/item. El año en curso lo recorta el
    // backend al mes del header.
    async fetchExpensesComparison({ state, commit, rootState }) {
        commit("SET_COMPARISON_LOADING", true);
        commit("SET_COMPARISON_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const endYear = moment(endMonth).year();

            // Default a [añoActual, añoActual-1] la primera vez que se abre.
            let years = state.comparisonYears;
            if (!years || years.length === 0) {
                years = [endYear, endYear - 1];
                commit("SET_COMPARISON_YEARS", years);
            }

            const endDate = moment(endMonth).endOf("month").format("YYYY-MM-DD");
            const response = await getExpensesComparison(endDate, years);
            const {
                months,
                series,
                available_years: availableYears,
            } = response.data;

            commit("SET_COMPARISON_DATA", Object.freeze({
                months: Object.freeze(months || []),
                series: Object.freeze((series || []).map(Object.freeze)),
                availableYears: Object.freeze(availableYears || []),
            }));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[expensesReport]", silent: true });
            commit(
                "SET_COMPARISON_ERROR",
                extractErrorMessage(error, "Error al cargar la comparación."),
            );
        } finally {
            commit("SET_COMPARISON_LOADING", false);
        }
    },

    async fetchExpensesSummary({ commit, rootState }) {
        commit("SET_SUMMARY_LOADING", true);
        commit("SET_SUMMARY_ERROR", null);

        try {
            const endDate = moment(rootState.reportsFilters.endMonth)
                .endOf("month")
                .format("YYYY-MM-DD");

            const response = await getExpensesSummaryCards(endDate);
            commit("SET_SUMMARY_CARDS", Object.freeze(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[expensesReport]", silent: true });
            commit(
                "SET_SUMMARY_ERROR",
                extractErrorMessage(error, "Error al cargar resumen de gastos.")
            );
        } finally {
            commit("SET_SUMMARY_LOADING", false);
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
